import { createSlice } from '@reduxjs/toolkit';

const SESSION_DURATION = 10 * 60 * 60 * 1000; // 10 hours in milliseconds

const getStoredUser = () => {
    try {
        const stored = localStorage.getItem('userSession');
        if (stored) {
            const session = JSON.parse(stored);
            // Check if session has expired (10 hours limit)
            if (new Date().getTime() > session.expiry) {
                localStorage.removeItem('userSession');
                return null;
            }
            return session.data;
        }
        
        // Backwards compatibility: clear old unprotected keys
        localStorage.removeItem('user');
        sessionStorage.removeItem('user');
        
        return null;
    } catch {
        return null;
    }
};

const getStoredRole = () => {
    const user = getStoredUser();
    return user ? user.role : null;
};

const initialState = {
    status: 'idle',
    userDetails: [],
    tempDetails: [],
    loading: false,
    // Use sessionStorage - persists on refresh but cleared on new browser session
    currentUser: getStoredUser(),
    currentRole: getStoredRole(),
    error: null,
    response: null,
    darkMode: true
};

const userSlice = createSlice({
    name: 'user',
    initialState,
    reducers: {
        authRequest: (state) => {
            state.status = 'loading';
        },
        underControl: (state) => {
            state.status = 'idle';
            state.response = null;
        },
        stuffAdded: (state, action) => {
            state.status = 'added';
            state.response = null;
            state.error = null;
            state.tempDetails = action.payload;
        },
        authSuccess: (state, action) => {
            state.status = 'success';
            state.currentUser = action.payload;
            state.currentRole = action.payload.role;
            
            // Create a secure session object with a 10-hour expiration
            const sessionData = {
                data: action.payload,
                expiry: new Date().getTime() + SESSION_DURATION
            };
            
            localStorage.setItem('userSession', JSON.stringify(sessionData));
            
            // Clear old unsafe storage keys
            localStorage.removeItem('user');
            sessionStorage.removeItem('user');
            
            state.response = null;
            state.error = null;
        },
        authFailed: (state, action) => {
            state.status = 'failed';
            state.response = action.payload;
        },
        authError: (state, action) => {
            state.status = 'error';
            state.error = action.payload;
        },
        authLogout: (state) => {
            localStorage.removeItem('userSession');
            localStorage.removeItem('user');
            sessionStorage.removeItem('user');
            state.currentUser = null;
            state.status = 'idle';
            state.error = null;
            state.currentRole = null;
        },

        doneSuccess: (state, action) => {
            state.userDetails = action.payload;
            state.loading = false;
            state.error = null;
            state.response = null;
        },
        getDeleteSuccess: (state) => {
            state.loading = false;
            state.error = null;
            state.response = null;
        },

        getRequest: (state) => {
            state.loading = true;
        },
        getFailed: (state, action) => {
            state.response = action.payload;
            state.loading = false;
            state.error = null;
        },
        getError: (state, action) => {
            state.loading = false;
            state.error = action.payload;
        },
        toggleDarkMode: (state) => {
            state.darkMode = !state.darkMode;
        }
    },
});

export const {
    authRequest,
    underControl,
    stuffAdded,
    authSuccess,
    authFailed,
    authError,
    authLogout,
    doneSuccess,
    getDeleteSuccess,
    getRequest,
    getFailed,
    getError,
    toggleDarkMode
} = userSlice.actions;

export const userReducer = userSlice.reducer;
